export type SchemaMetaFieldType = 'string' | 'textarea' | 'number' | 'boolean' | 'date';

export interface SchemaMetaTemplateField {
  key: string;
  displayName: string;
  description?: string;
  mandatory?: boolean;
  type: SchemaMetaFieldType;
}

export interface SchemaMetaTemplate {
  fields: SchemaMetaTemplateField[];
}

export type SchemaFieldMeta = Record<string, unknown>;

export const EMPTY_SCHEMA_META_TEMPLATE: SchemaMetaTemplate = { fields: [] };

export function isValidSchemaMetaKey(key: string): boolean {
  return /^[a-zA-Z0-9_]+$/.test(key);
}

export function isValidSchemaMetaTemplate(template: SchemaMetaTemplate): boolean {
  const keys = new Set<string>();
  return template.fields.every(field => {
    const key = field.key;
    const displayName = field.displayName?.trim();
    if (!key || !isValidSchemaMetaKey(key) || !displayName || keys.has(key)) return false;
    keys.add(key);
    return true;
  });
}

export function isMissingMetaValue(value: unknown): boolean {
  return value === undefined || value === null || value === '';
}

interface SchemaMetaNode {
  key: string;
  meta?: SchemaFieldMeta;
  properties?: SchemaMetaNode[];
  items?: { properties?: SchemaMetaNode[] };
}

export function getMissingMandatoryMetaFields(
  nodes: SchemaMetaNode[],
  metaTemplate: SchemaMetaTemplate
): string[] {
  const mandatoryFields = metaTemplate.fields.filter(field => field.mandatory && field.key);
  if (!mandatoryFields.length) return [];

  const missing: string[] = [];
  const checkNodes = (items: SchemaMetaNode[], parentPath = ''): void => {
    items.forEach(node => {
      const path = parentPath ? `${parentPath}.${node.key}` : node.key;
      mandatoryFields.forEach(field => {
        const value = node.meta?.[field.key];
        if (isMissingMetaValue(value)) {
          missing.push(`${path}: ${field.displayName || field.key}`);
        }
      });
      checkNodes(node.properties || node.items?.properties || [], path);
    });
  };

  checkNodes(nodes || []);
  return missing;
}
